import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftOutlined, 
  Loading3QuartersOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  EyeOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { 
  Avatar, 
  Badge, 
  Button, 
  Col, 
  Empty, 
  notification, 
  Row, 
  Space, 
  Pagination, 
  Input,
  Card,
  Typography,
  Tag,
  Divider,
  Spin,
  Tooltip
} from 'antd';
import Loader from '../compoinents/loader';
import { myRequestObj } from '../../assets/utils/lib';
import { uri_img } from '../../assets/utils/http-request';
import { FixedSizeList as List } from 'react-window';
import StandardFilter from '../compoinents/StandardFilter';
import { useStandardFilter } from '../compoinents/useStandardFilter';
import { CLAIMS_FILTERS } from '../compoinents/FilterConfigs';
import {
  getDisplayCost,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../../components/Claims/claimFinancialHelpers';

// Import the modern SCSS styles
import '../../styles/main.scss';

const { Title, Text } = Typography;

// Modern Claim Card Component
const ClaimCard = ({ claim, onClick, extended = true }) => {
  console.log('claim', claim);
  const getStatusColor = (status) => {
    const colors = {
      'Pending': '#ff9200',
      'Queried': '#ff4d4f',
      'Approved': '#52c41a',
      'Declined': '#ff4d4f'
    };
    return colors[status] || '#d9d9d9';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': <Loading3QuartersOutlined />,
      'Queried': <FileTextOutlined />,
      'Approved': <FileTextOutlined />,
      'Declined': <FileTextOutlined />
    };
    return icons[status] || <FileTextOutlined />;
  };

  const getPriorityColor = () => {
    // Strong visual for HM review states
    if (Number(claim?.review_required) === 1) return '#ff4d4f';      // Returned by HM
    if (Number(claim?.review_required) === 2) return '#faad14';      // Awaiting HM review
    // Fallback to existing logic
    if (claim.queries > 0) return '#ff4d4f';
    if (claim.aggregatedItemsStatus?.cat?.Pending?.count > 0) return '#ff9200';
    if (claim.aggregatedItemsStatus?.cat?.Approved?.count > 0) return '#52c41a';
    return '#d9d9d9';
  };

  // HM review flag status
  const reviewRequired = Number(claim?.review_required ?? 0);

  // HM comments payload (all comments, including medical), pipe-separated
  const rawHMComments =
    typeof claim?.claims_comment === 'string' ? claim.claims_comment : '';
  const commentSegments = rawHMComments
    .split('|break|')
    .map((s) => s.trim())
    .filter((s) => s);

  // Extract medical comments (lines starting with "Medical comment")
  const medicalPrefix = 'medical comment';
  const medicalSegments = commentSegments.filter((s) =>
    s.toLowerCase().startsWith(medicalPrefix)
  );

  // Get source meta information (source_mode takes precedence, fallback to source)
  const getSourceMeta = () => {
    const mode = String(
      claim?.source_mode ||
      (claim?.source !== undefined ? (Number(claim.source) === 1 ? 'manual' : 'direct') : 'direct')
    ).toLowerCase();
    
    return {
      mode,
      label: mode === 'manual' ? 'Manual' : mode === 'direct' ? 'Direct' : 'Mixed',
      color: mode === 'manual' ? 'orange' : mode === 'direct' ? 'green' : 'purple',
      manualCount: Number(claim?.manual_items_count || 0),
      directCount: Number(claim?.direct_items_count || 0),
    };
  };

  const hasHMReview = reviewRequired === 1;
  const isHMResolutionPending = reviewRequired === 2;

  const hmRejectionSnippet =
    (medicalSegments.length ? medicalSegments[medicalSegments.length - 1] : '') ||
    (commentSegments.length ? commentSegments[commentSegments.length - 1] : '');

  console.log('hmRejectionSnippet', hmRejectionSnippet);
  console.log('medicalSegments', medicalSegments);
  console.log('commentSegments', commentSegments);
  console.log('reviewRequired', reviewRequired);
  return (
    <Card 
      className="claim-card"
      hoverable
      onClick={onClick}
      style={{ 
        marginBottom: '16px',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid #f0f0f0',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        position: 'relative'
      }}
    >
      {/* Priority Indicator */}
      <div 
        className="priority-indicator"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '4px',
          background: getPriorityColor(),
          zIndex: 2
        }}
      />

      {/* Status Ribbon */}
      {claim.aggregatedItemsStatus && (
        <Badge.Ribbon
          text={
            <span style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>
              {hasHMReview
                ? 'Returned by HM'
                : isHMResolutionPending
                  ? 'Awaiting HM review'
                  : claim.flag.text}
            </span>
          }
          color={
            hasHMReview
              ? '#ff4d4f'
              : isHMResolutionPending
                ? '#faad14'
                : claim.flag.color
          }
          placement="end"
          style={{ top: '12px' }}
        />
      )}

      <div className="claim-card-content">
        {/* Header Section */}
        <div className="claim-header">
          <div className="patient-section">
            <div className="patient-avatar-wrapper">
              <Avatar 
                size={56} 
                src={claim.pic_name ? `${uri_img}${claim.pic_name}` : undefined}
                icon={<UserOutlined />}
                style={{ 
                  border: '3px solid #ffffff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
              />
              {claim.queries > 0 && (
                <Badge 
                  count={claim.queries} 
                  offset={[-6, 6]}
                  style={{ 
                    backgroundColor: '#ff4d4f',
                    boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}
                />
              )}
            </div>
            
            <div className="patient-details">
              <Title level={4} style={{ 
                margin: 0, 
                color: '#1a1a1a',
                fontWeight: 600,
                lineHeight: 1.2,
                fontSize: '16px'
              }}>
                {claim?.surname?.__capitalize()}, {claim?.first_name?.__capitalize()}
              </Title>
              <Text type="secondary" style={{ 
                fontSize: '12px',
                display: 'block',
                marginTop: '2px',
                color: '#666',
                fontWeight: 500
              }}>
                {claim?.middle_name?.__capitalize()}
              </Text>
              
              <div className="claim-meta">
                <Tag 
                  color="blue" 
                  size="small"
                  style={{ 
                    borderRadius: '6px',
                    fontSize: '10px',
                    fontWeight: 600,
                    marginTop: '6px',
                    padding: '2px 8px',
                    height: '20px',
                    lineHeight: '16px',
                    border: 'none',
                    boxShadow: '0 1px 4px rgba(24, 144, 255, 0.2)'
                  }}
                >
                  ID: {claim.claims_id}
                </Tag>
                {extended && (
                  <Tag 
                    color={(() => {
                      const sourceMeta = getSourceMeta();
                      return sourceMeta.color;
                    })()} 
                    size="small"
                    style={{ 
                      borderRadius: '6px',
                      fontSize: '10px',
                      fontWeight: 600,
                      marginTop: '6px',
                      marginLeft: '4px',
                      padding: '2px 8px',
                      height: '20px',
                      lineHeight: '16px',
                      border: 'none',
                      boxShadow: '0 1px 4px rgba(255, 152, 0, 0.2)'
                    }}
                  >
                    {(() => {
                      const sourceMeta = getSourceMeta();
                      return sourceMeta.label;
                    })()}
                  </Tag>
                )}
                {hasHMReview && (
                  <Tag
                    color="red"
                    style={{
                      borderRadius: '6px',
                      fontSize: '10px',
                      fontWeight: 600,
                      marginTop: '6px',
                      marginLeft: '4px',
                      padding: '2px 8px',
                      height: '20px',
                      lineHeight: '16px',
                      border: 'none',
                      boxShadow: '0 1px 4px rgba(255, 77, 79, 0.25)'
                    }}
                  >
                    Review required (HM)
                  </Tag>
                )}
                {isHMResolutionPending && (
                  <Tag
                    color="gold"
                    style={{
                      borderRadius: '6px',
                      fontSize: '10px',
                      fontWeight: 600,
                      marginTop: '6px',
                      marginLeft: '4px',
                      padding: '2px 8px',
                      height: '20px',
                      lineHeight: '16px',
                      border: 'none',
                      boxShadow: '0 1px 4px rgba(250, 173, 20, 0.25)'
                    }}
                  >
                    Awaiting HM review
                  </Tag>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="claim-content">
          <Row gutter={[16, 12]} align="middle">
            {/* Date Information */}
            {extended && (
              <Col xs={24} sm={12} md={6}>
                <div className="info-section">
                  <div className="info-label">
                    <CalendarOutlined style={{ color: '#52c41a', marginRight: '4px', fontSize: '12px' }} />
                    <Text strong style={{ fontSize: '11px', color: '#666', fontWeight: 600 }}>
                      Date
                    </Text>
                  </div>
                  <div className="info-value">
                    <Text style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: 600 }}>
                      {new Date(claim?.claims_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </Text>
                  </div>
                </div>
              </Col>
            )}

            {/* PAR Items Count */}
            <Col xs={12} sm={12} md={4}>
              <div className="info-section">
                <div className="info-label">
                  <FileTextOutlined style={{ color: '#1890ff', marginRight: '4px', fontSize: '12px' }} />
                  <Text strong style={{ fontSize: '11px', color: '#666', fontWeight: 600 }}>
                    Items
                  </Text>
                </div>
                <div className="info-value">
                  <Text strong style={{ 
                    fontSize: '18px', 
                    color: '#1890ff',
                    fontWeight: 700
                  }}>
                    {claim?.pa_items_count}
                  </Text>
                </div>
              </div>
            </Col>

            {/* Financial totals */}
            <Col xs={24} sm={12} md={10}>
              <div className="info-section">
                <div className="info-label">
                  <DollarOutlined style={{ color: '#52c41a', marginRight: '4px', fontSize: '12px' }} />
                  <Text strong style={{ fontSize: '11px', color: '#666', fontWeight: 600 }}>
                    Amounts
                  </Text>
                </div>
                <div className="info-value" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Text style={{ fontSize: '12px', color: '#64748b' }}>
                    Submitted ₦{getSubmittedTotal(claim).toLocaleString()}
                  </Text>
                  <Text style={{ fontSize: '12px', color: '#64748b' }}>
                    PA approved ₦{getPaApprovedTotal(claim).toLocaleString()}
                  </Text>
                  <Text strong style={{ fontSize: '15px', color: '#52c41a', fontWeight: 700 }}>
                    Claims approved ₦{getDisplayCost(claim).toLocaleString()}
                  </Text>
                </div>
              </div>
            </Col>

            {/* HM Rejection / Review Snippet (from HM "Medical comment" line in claims_comment) */}
            {(hasHMReview || isHMResolutionPending) && hmRejectionSnippet && (
              <Col xs={24}>
                <div className="info-section">
                  <div className="info-label">
                    <Text strong style={{ fontSize: '11px', color: '#b91c1c', fontWeight: 600 }}>
                      {hasHMReview ? 'Returned by HM' : 'Awaiting HM review'}
                    </Text>
                  </div>
                  <div className="info-value">
                    <Text
                      style={{
                        fontSize: '12px',
                        color: '#7f1d1d',
                        fontWeight: 500,
                        display: 'block'
                      }}
                      ellipsis={{ tooltip: hmRejectionSnippet?.replace(/>>>/g, '→') }}
                    >
                      {hmRejectionSnippet?.replace(/>>>/g, '→')}
                    </Text>
                  </div>
                </div>
              </Col>
            )}

            {/* Status Summary */}
            <Col xs={24} sm={24} md={8}>
              <div className="status-summary">
                {claim.aggregatedItemsStatus?.cat && (
                  <div className="status-pills">
                    {Object.entries(claim.aggregatedItemsStatus.cat)
                      .filter(([status, data]) => data.count > 0) // Only show non-zero statuses
                      .slice(0, 3)
                      .map(([status, data]) => (
                        <Tag 
                          key={status}
                          color={getStatusColor(status)}
                          icon={getStatusIcon(status)}
                          style={{ 
                            margin: '2px',
                            borderRadius: '6px',
                            border: 'none',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '10px',
                            padding: '2px 6px',
                            height: '20px',
                            lineHeight: '16px',
                            boxShadow: `0 1px 4px ${getStatusColor(status)}40`
                          }}
                        >
                          {data.count} {data.text}
                        </Tag>
                      ))}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>

        {/* Footer Section */}
        <div className="claim-footer">
          <div className="footer-info">
            <Text type="secondary" style={{ fontSize: '10px', color: '#999', fontWeight: 500 }}>
              Submitted: {new Date(claim?.claims_date).toLocaleDateString()}
            </Text>
          </div>
          <div className="footer-actions">
            <Button 
              type="text" 
              size="small"
              style={{ 
                fontSize: '10px',
                color: '#666',
                padding: '2px 6px',
                fontWeight: 500,
                height: '20px',
                marginRight: '8px'
              }}
            >
              Actions
            </Button>
            <Button 
              type="primary" 
              icon={<EyeOutlined />}
              size="small"
              style={{ 
                borderRadius: '6px',
                fontWeight: 600,
                height: '24px',
                padding: '0 8px',
                boxShadow: '0 1px 4px rgba(24, 144, 255, 0.25)',
                fontSize: '10px',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none'
              }}
            >
              View
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Facility Header Component
const FacilityHeader = ({ facility, loading, onRefresh }) => {
  if (loading) {
    return (
      <Card 
        className="facility-header-card loading"
        style={{ 
          marginBottom: '24px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          minHeight: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Spin size="large" />
      </Card>
    );
  }

  if (!facility?.facility_name) {
    return (
      <Card 
        className="facility-header-card error"
        style={{ 
          marginBottom: '24px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
          color: 'white'
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text style={{ color: 'white', fontSize: '16px' }}>
            Facility information not available
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      className="facility-header-card"
      style={{ 
        marginBottom: '24px',
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '100%',
          background: 'radial-gradient(circle at 70% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          zIndex: 1
        }}
      />
      
      <Row gutter={[16, 16]} align="middle" style={{ position: 'relative', zIndex: 2 }}>
        <Col xs={24} sm={16} md={18}>
          <div className="facility-info">
            <Title level={3} style={{ 
              color: 'white', 
              margin: 0,
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: 700
            }}>
              {facility?.facility_name}
            </Title>
            <div style={{ marginTop: '12px' }}>
              <Space size="large" wrap>
                <div className="facility-detail">
                  <EnvironmentOutlined style={{ marginRight: '6px', fontSize: '16px' }} />
                  <Text style={{ color: 'white', fontWeight: 500 }}>
                    {facility?.facility_city}, {facility?.facility_state}
                  </Text>
                </div>
                <div className="facility-detail">
                  <MailOutlined style={{ marginRight: '6px', fontSize: '16px' }} />
                  <a 
                    href={`mailto:${facility?.facility_email}`}
                    style={{ 
                      color: 'white', 
                      textDecoration: 'underline',
                      fontWeight: 500,
                      transition: 'opacity 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {facility?.facility_email}
                  </a>
                </div>
                <div className="facility-detail">
                  <PhoneOutlined style={{ marginRight: '6px', fontSize: '16px' }} />
                  <a 
                    href={`tel:${facility?.facility_phone}`}
                    style={{ 
                      color: 'white', 
                      textDecoration: 'underline',
                      fontWeight: 500,
                      transition: 'opacity 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {facility?.facility_phone}
                  </a>
                </div>
              </Space>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <div className="facility-actions">
            <Space>
              <Button 
                type="primary" 
                ghost
                icon={<ReloadOutlined />}
                size="small"
                onClick={onRefresh}
                style={{ 
                  borderRadius: '8px',
                  fontWeight: 600,
                  border: '2px solid rgba(255,255,255,0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.6)';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Refresh
              </Button>
            </Space>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

const FacilityClaims = ({ extended = true }) => {
  const [loading, setLoading] = useState(false);
  const [targetFacility, setTargetFacility] = useState({});
  const [targetFacilityData, setTargetFacilityData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  // const [searchTerm, setSearchTerm] = useState(''); // Removed - using filter search instead
  // const [searchLoading, setSearchLoading] = useState(false); // Removed - using filter loading instead
  
  const navigate = useNavigate();
  const { facility, segment } = useParams();
  const fetchOperation = useRef(false);
  // const searchTimeout = useRef(null); // Removed - using filter debouncing instead

  // StandardFilter integration
  const {
    filters,
    loading: filterLoading,
    activeFiltersCount,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    getFormattedFilters
  } = useStandardFilter({
    initialFilters: {
      search: '',
      fromDate: null,
      toDate: null,
      insuranceNo: '',
      minCost: null,
      maxCost: null
    },
    autoApply: true,
    debounceDelay: 500,
    onApplyFilters: async (filterValues) => {
      const formattedFilters = getFormattedFilters(filterValues);
      const searchValue = filterValues.search || '';
      
      // Fetch claims with filters applied
      const params = getDecodedParams();
      if (!params) return;
      
      await fetchClaimsList(params.entry, params.seg, true, 1, searchValue, formattedFilters);
    },
    onClearFilters: async () => {
      const params = getDecodedParams();
      if (!params) return;
      
      await fetchClaimsList(params.entry, params.seg, true, 1, '', {});
    }
  });

  // Decode facility and segment
  const getDecodedParams = useCallback(() => {
    try {
      const entry = atob(facility);
      const seg = atob(segment);
      return { entry, seg };
    } catch (e) {
      notification.error({
        message: 'Error',
        description: 'Invalid facility or segment parameters'
      });
      return null;
    }
  }, [facility, segment]);

  // Load facility data
  const loadFacility = useCallback(async (entry) => {
    if (!entry) return;
    
    try {
      const response = await myRequestObj('loadFacility').post({ entry });
      const data = response?.data;
      
      if (!data?.status && Number(data?.error) === 0) {
        setTargetFacility(data?.data);
      } else {
        notification.error({
          message: 'Warning',
          description: 'Unable to load facility data. You can continue fetching claims or use the refresh button.'
        });
      }
    } catch (error) {
      console.error('Error loading facility:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to load facility data'
      });
    }
  }, []);

  // Fetch claims list
  const fetchClaimsList = useCallback(async (entry, segment, showLoader = false, page = 1, search = '', filterParams = {}) => {
    if (fetchOperation.current) return;
    
    if (showLoader) setLoading(true);
    fetchOperation.current = true;

    try {
      const response = await myRequestObj('aClaimsListByFacility').post({ 
        entry, 
        segment, 
        page, 
        search,
        ...filterParams
      });
      
      const body = response?.data;
      setTargetFacilityData({ data: body?.data });
      setPagination({ 
        current: body?.pagination?.currentPage, 
        pageSize: body?.pagination?.perPage, 
        total: body?.pagination?.total, 
        lastPage: body?.pagination?.lastPage 
      });
    } catch (error) {
      console.error('Error fetching claims:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch claims data'
      });
    } finally {
      if (showLoader) setLoading(false);
      fetchOperation.current = false;
    }
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    const params = getDecodedParams();
    if (!params) return;
    
    setPagination(prev => ({ ...prev, current: page }));
    const formattedFilters = getFormattedFilters(filters);
    const searchValue = filters.search || '';
    fetchClaimsList(params.entry, params.seg, true, page, searchValue, formattedFilters);
  }, [getDecodedParams, fetchClaimsList, getFormattedFilters, filters]);

  // Handle search with debouncing - removed, using filter search instead
  // const handleSearch = useCallback((value) => {
  //   setSearchTerm(value);
  //   setSearchLoading(true);
  //   
  //   if (searchTimeout.current) {
  //     clearTimeout(searchTimeout.current);
  //   }
  //   
  //   searchTimeout.current = setTimeout(() => {
  //     const params = getDecodedParams();
  //     if (!params) return;
  //     
  //     const formattedFilters = getFormattedFilters(filters);
  //     fetchClaimsList(params.entry, params.seg, true, 1, value, formattedFilters);
  //     setSearchLoading(false);
  //   }, 500);
  // }, [getDecodedParams, fetchClaimsList, getFormattedFilters, filters]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    const params = getDecodedParams();
    if (!params) return;
    
    loadFacility(params.entry);
    const formattedFilters = getFormattedFilters(filters);
    const searchValue = filters.search || '';
    fetchClaimsList(params.entry, params.seg, true, 1, searchValue, formattedFilters);
  }, [getDecodedParams, loadFacility, fetchClaimsList, getFormattedFilters, filters]);

  // Handle claim click
  const handleClaimClick = useCallback((claim) => {
    navigate(`/claims/filings/?encounter=${claim.checkin_id}`, { 
      state: { facility, segment } 
    });
  }, [navigate, facility, segment]);

  // Initial load
  useEffect(() => {
    const params = getDecodedParams();
    if (!params) return;
    
    loadFacility(params.entry);
    fetchClaimsList(params.entry, params.seg, true);
  }, [getDecodedParams, loadFacility, fetchClaimsList]);

  // Cleanup - removed searchTimeout cleanup since we're using filter debouncing
  // useEffect(() => {
  //   return () => {
  //     if (searchTimeout.current) {
  //       clearTimeout(searchTimeout.current);
  //     }
  //   };
  // }, []);

  return (
    <div className="facility-claims-container">
      {/* Header */}
      <div className="facility-claims-header">
        <div className="header-controls">
          <Button 
            onClick={() => navigate('/claims')} 
            icon={<ArrowLeftOutlined />}
            style={{ 
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            Back to Claims
          </Button>
        </div>
      </div>

      {/* Facility Information */}
      <FacilityHeader facility={targetFacility} loading={loading} onRefresh={handleRefresh} />

      {/* Standard Filter Component */}
      <StandardFilter
        filters={CLAIMS_FILTERS.facility}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        initialValues={filters}
        loading={filterLoading}
        collapsible={true}
        defaultExpanded={false}
        title="Advanced Filters"
        showActiveCount={true}
        showSearchIcon={true}
        style={{ marginBottom: '24px' }}
      />

      {/* Controls and Pagination */}
      <div className="claims-controls">
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} sm={24} md={12} lg={8}>
            <div className="results-info-container">
              <div className="results-info">
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                  {pagination.total > 0 ? (
                    `Showing ${((pagination.current - 1) * pagination.pageSize) + 1}-${Math.min(pagination.current * pagination.pageSize, pagination.total)} of ${pagination.total} claims`
                  ) : 'No claims found'}
                </Text>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={24} md={12} lg={16}>
            <div className="pagination-wrapper" style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger={false}
                disabled={loading}
                showTotal={(total, range) => (
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'var(--ant-text-color-secondary, #64748b)',
                    fontWeight: 500,
                    marginRight: '12px',
                    whiteSpace: 'nowrap'
                  }}>
                    {range[0]}-{range[1]} of {total}
                  </div>
                )}
                size="small"
                responsive={{
                  xs: false,
                  sm: false,
                  md: true,
                  lg: true,
                  xl: true,
                  xxl: true
                }}
                style={{ 
                  flex: '1',
                  minWidth: '300px',
                  justifyContent: 'flex-end'
                }}
                itemRender={(current, type, originalElement) => {
                  if (type === 'prev') {
                    return (
                      <Button 
                        size="small" 
                        style={{ 
                          borderRadius: '6px',
                          height: '24px',
                          minWidth: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ‹
                      </Button>
                    );
                  }
                  if (type === 'next') {
                    return (
                      <Button 
                        size="small" 
                        style={{ 
                          borderRadius: '6px',
                          height: '24px',
                          minWidth: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ›
                      </Button>
                    );
                  }
                  if (type === 'page') {
                    return (
                      <Button 
                        size="small" 
                        type={current === pagination.current ? 'primary' : 'default'}
                        style={{ 
                          borderRadius: '6px',
                          height: '24px',
                          minWidth: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: current === pagination.current ? 600 : 500
                        }}
                      >
                        {current}
                      </Button>
                    );
                  }
                  return originalElement;
                }}
              />
            </div>
          </Col>
        </Row>
      </div>

      {/* Claims List */}
      <div className="claims-content">
        {loading ? (
          <div className="loading-container">
            <Loader />
          </div>
        ) : targetFacilityData?.data?.length > 0 ? (
          <div className="claims-list">
            {targetFacilityData.data.map((claim, index) => (
              <ClaimCard
                key={`${claim.checkin_id}-${index}`}
                claim={claim}
                onClick={() => handleClaimClick(claim)}
                extended={extended}
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Empty 
              description={
                <div>
                  <Text type="secondary">No claims found</Text>
                  {filters.search && (
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">
                        No claims matching "{filters.search}"
                      </Text>
                    </div>
                  )}
                </div>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilityClaims;